/**
 * @file Current user data display component
 * @version 1.0.0
 * @since 2025-08-25
 */

import { useUser } from '../composables/user.js';

export default {
  name: 'UserData',

  template: `
    <div>
      <!-- Loading State -->
      <div v-if="user.isLoading" class="d-flex align-items-center text-muted py-2">
        <i class="bi bi-hourglass-split me-2"></i>
        Loading user...
      </div>
      
      <!-- User Data Layout -->
      <div v-else class="d-flex justify-content-between align-items-center">
        <!-- User Info Section -->
        <div class="d-flex align-items-center">
          <i class="bi bi-person-circle display-4 text-secondary me-3"></i>
          <div>
            <div class="fw-semibold">{{ user.name }}</div>
            <div v-if="user.email" class="text-muted small">{{ user.email }}</div>
            <div class="small text-muted">{{ user.id }}</div>
            
            <!-- Groups (with roles as secondary info) -->
            <div v-if="user.groups.length > 0" class="mt-1">
              <span 
                v-for="group in getUserGroupsWithMetadata()" 
                :key="group.id"
                class="badge me-1"
                :class="getGroupBadgeClass(group)"
                :title="getGroupDescription(group)"
              >
                <i v-if="group.icon" :class="'bi bi-' + group.icon + ' me-1'"></i>
                {{ group.name }}
              </span>
            </div>
            <div v-else class="text-muted small mt-1">
              <i class="bi bi-person-x me-1"></i>
              No groups assigned
            </div>
          </div>
        </div>
      </div>
    </div>
  `,

  setup() {
    const user = useUser();

    const getUserGroupsWithMetadata = () => {
      return user.groups.map(groupId => {
        const groupDef = user.availableGroups.find(g => g.id === groupId);
        return (
          groupDef || {
            id: groupId,
            name: groupId,
            description: '',
            color: 'secondary',
          }
        );
      });
    };

    const getGroupDescription = group => {
      return group.description || '';
    };

    const getGroupBadgeClass = group => {
      const color = group.color || 'secondary';
      return `bg-${color}`;
    };

    return {
      user,
      getUserGroupsWithMetadata,
      getGroupDescription,
      getGroupBadgeClass,
    };
  },
};
